/**
 * <Worklist> — everything waiting on the signed-in user, in one panel.
 *
 * Approvals are a FILTER here, not a separate screen. As far as a person is
 * concerned, approving a purchase order and receiving goods against one are
 * the same kind of obligation: something is waiting on them. Splitting those
 * across two inboxes is a fact about our storage, not about their day.
 *
 * Every row leads with the VERB, because the useful question is not "what is
 * this record" but "what am I being asked to do". Clicking the primary button
 * fetches the task's vetted seed and hands both to the host, which does the
 * navigation it already knows how to do (`TaskOpener`). The panel never
 * navigates itself — that differs per host and would make this component
 * un-shareable.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import {
  AlertTriangle, ArrowLeft, Bot, CheckCircle2, ClipboardList, FileText, Inbox,
  Info, Loader2, Phone, RefreshCw, UserPlus, Users, Wand2, X,
} from 'lucide-react'

import AssignTaskDialog from './AssignTaskDialog'
import PlaybookForm from '../playbooks/PlaybookForm'
import type { FormField, PlaybookTaskRuntime } from '../playbooks/contract'
import { readablePerson } from '../../dms/people'
import type {
  DocumentRowInfo, TaskOpener, WorkTask, WorklistRuntime, WorklistCounts,
} from './contract'

export interface WorklistProps {
  runtime: WorklistRuntime
  onClose: () => void
  onOpenTask: TaskOpener
  /** Lifts the badge count so the top bar stays in step after an action. */
  onCountChange?: (count: number) => void
  /**
   * The host's full approvals surface, rendered as a TAB of this panel — the
   * same inbox the designer's live preview shows, embedded rather than
   * stacked on top. A modal over a panel reads as a second place; the user
   * came from a row two inches away and expects to be able to walk back.
   *
   * `instanceId` is the row they clicked, so the inbox opens on that record
   * (a document approval included — same table, same door). Unbound, the
   * Approvals filter stays an ordinary in-panel filter.
   */
  renderApprovals?: (ctx: { instanceId: string; scope: 'mine' | 'all' }) => ReactNode
  /**
   * The host's document surface, rendered as a tab of this panel — the same
   * viewer, metadata and lifecycle buttons the repository shows.
   *
   * A document waiting for a sign-off has no approval instance to review yet,
   * so the approvals inbox has nothing to open. Sending the reader out to the
   * repository instead would close the panel and lose the rest of their
   * queue, which is the one thing a worklist exists to keep.
   */
  renderDocument?: (ctx: { repo: string; documentId: string }) => ReactNode
  /**
   * Answers a task an agent playbook raised (`target.open === 'playbook_input'`).
   * The task's form opens IN the panel; answering it resumes the playbook run
   * that is waiting on it. Without this, such a task opens like any other.
   */
  playbookTasks?: PlaybookTaskRuntime
  /**
   * Resumes a suspended / waiting Transaction Wizard run
   * (`target.open === 'wizard_resume'`): the host mounts the shared
   * WizardRunner with `resumeRunId`. Without this, such a task goes to
   * `onOpenTask` like any other.
   */
  onResumeWizard?: (req: { wizard: string; runId: string; task: WorkTask }) => void | Promise<void>
  /** Poll interval in ms; 0 disables. Default 30s, matching the approvals badge. */
  pollMs?: number
}

type FilterKey = 'all' | 'todo' | 'approval' | 'document' | 'alert'

/**
 * Filters describe what the user is looking for, not which provider produced
 * the row — a person looking for "documents" does not care that a version
 * approval and a periodic review come from two providers.
 */
const FILTERS: Array<{ key: FilterKey; label: string; kinds?: string[] }> = [
  { key: 'all', label: 'All' },
  { key: 'todo', label: 'To do', kinds: ['work', 'todo', 'reminder'] },
  { key: 'approval', label: 'Approvals', kinds: ['approval'] },
  { key: 'document', label: 'Documents', kinds: ['document', 'document_review'] },
  { key: 'alert', label: 'Alerts', kinds: ['job_failure', 'delivery_failure', 'proposal'] },
]

function iconFor(kind: string) {
  if (kind === 'approval') return Inbox
  if (kind === 'document' || kind === 'document_review') return FileText
  if (kind === 'reminder') return Phone
  if (kind === 'playbook_input') return Bot
  if (kind === 'wizard') return Wand2
  if (kind === 'job_failure' || kind === 'delivery_failure') return AlertTriangle
  return ClipboardList
}

/** "in 3 days" / "2 days ago" — a due date is only useful as a distance. */
function dueLabel(iso?: string | null): string {
  if (!iso) return ''
  const due = new Date(iso)
  if (Number.isNaN(due.getTime())) return ''
  const days = Math.round((due.getTime() - Date.now()) / 86400000)
  if (days === 0) return 'today'
  if (days === 1) return 'tomorrow'
  if (days === -1) return 'yesterday'
  if (days > 1) return `in ${days} days`
  return `${Math.abs(days)} days ago`
}

/** "ravi, finance" — who a task is waiting on, roles included. */
function waitingOn(task: WorkTask): string {
  const who = (task.assignees || [])
    .map((a) => String(a?.id || '').trim())
    .filter(Boolean)
  if (who.length === 0) return ''
  return who.length > 3 ? `${who.slice(0, 3).join(', ')} +${who.length - 3}` : who.join(', ')
}

interface ApprovalMeta {
  fields: Array<{ label: string; value: string }>
  submitter: string
  when: string
  request: string
  step: string
}

/**
 * The detail an approvals-inbox row carries, read off the task's mirrored
 * `context`. The worklist does not re-query the approval store for it: the
 * provider already wrote it into the index, which is what keeps the two
 * surfaces saying the same thing about one record.
 */
function approvalMeta(task: WorkTask, doc?: DocumentRowInfo): ApprovalMeta {
  const ctx = (task.context || {}) as Record<string, unknown>
  const rawFields = Array.isArray(ctx.fields) ? ctx.fields : []
  const fields = rawFields
    .filter((f): f is { label: string; value: string } =>
      !!f && typeof f === 'object'
      && typeof (f as { label?: unknown }).label === 'string'
      && typeof (f as { value?: unknown }).value === 'string')
    .slice(0, 4)
  const str = (v: unknown) => (typeof v === 'string' ? v.trim() : '')
  // A document row previews its REPOSITORY and what the decision does — the
  // staged payload is a pointer, so there are no header fields to show. The
  // repository's live answer wins over the mirrored one where it has one.
  if (task.target?.dms && fields.length === 0) {
    const repo = str(ctx.repo)
    const target = doc?.target_state || str(ctx.target_state)
    const does = str(ctx.dms_kind) === 'review'
      ? 'Review sign-off'
      : target ? `Releases as ${target}` : str(ctx.action_label) || 'Proposed release'
    if (repo) fields.push({ label: 'In', value: repo })
    fields.push({ label: 'Decision', value: does })
  }
  return {
    fields,
    submitter: str(ctx.submitter) || (doc?.submitted_by || ''),
    when: whenLabel(str(ctx.submitted_at)),
    request: task.target?.dms ? '' : str(ctx.action_label),
    step: str(ctx.step) || (doc?.pending_roles || ''),
  }
}

/** "2 hours ago" / "3 days ago" — when the approval was raised. */
function whenLabel(iso: string): string {
  if (!iso) return ''
  const at = new Date(iso)
  if (Number.isNaN(at.getTime())) return ''
  const mins = Math.round((Date.now() - at.getTime()) / 60000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins} min ago`
  const hours = Math.round(mins / 60)
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`
  const days = Math.round(hours / 24)
  return `${days} day${days === 1 ? '' : 's'} ago`
}

export default function Worklist({
  runtime, onClose, onOpenTask, onCountChange, renderApprovals, renderDocument,
  playbookTasks, onResumeWizard, pollMs = 30000,
}: WorklistProps) {
  const [filter, setFilter] = useState<FilterKey>('all')
  // Which tab is on screen. 'approvals' swaps the body for the host's
  // approvals surface; the filter row stays put, so coming back is a click on
  // any other pill — a tab change, not a dialog to dismiss.
  const [view, setView] = useState<'work' | 'approvals' | 'document' | 'playbook'>('work')
  // The playbook task whose form is open in the panel.
  const [pbTask, setPbTask] = useState<WorkTask | null>(null)
  const [approvalAt, setApprovalAt] = useState('')
  // The document open in the panel's document tab, when a row led to one.
  const [docAt, setDocAt] = useState<{ repo: string; documentId: string } | null>(null)
  // How much is open across the whole app, asked ONLY when an administrator's
  // own list is empty. An empty panel is ambiguous — "nothing was raised" and
  // "it was raised, on a role you do not hold" look identical, and the second
  // one reads as a broken worklist. Naming the number resolves it.
  const [elsewhere, setElsewhere] = useState<number | null>(null)
  // Whose queue is on screen. An administrator (or FUNC-ADMIN holder) can flip
  // to EVERYONE's open work — the same reach the approvals inbox has always
  // given them, and the only way to answer "who is this sitting with?" while
  // the person it is sitting with is away. `isSuper` is LEARNED from the list
  // response; the panel never assumes the right and the server re-checks it.
  const [isSuper, setIsSuper] = useState(false)
  const [scope, setScope] = useState<'mine' | 'all'>('mine')
  const [items, setItems] = useState<WorkTask[]>([])
  const [counts, setCounts] = useState<WorklistCounts | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [busyKey, setBusyKey] = useState('')
  // What the last action DID, kept on screen rather than thrown as a toast
  // that is gone before it is read. `recordKey` marks the row the work moved
  // to, so "press Approve again" is never something the user has to infer.
  const [notice, setNotice] = useState<{ text: string; recordKey?: string } | null>(null)
  // Document titles, keyed by approval instance id — a staged document is a
  // pointer, so its row cannot name itself (see `runtime.documents`).
  const [docRows, setDocRows] = useState<Record<string, DocumentRowInfo>>({})
  // "New task": the human-authored twin of a work rule. Offered only when the
  // host can say who the app's people are — an assignment dialog with an empty
  // picker is worse than no button.
  const [assignOpen, setAssignOpen] = useState(false)
  // user id -> a name a person can read (see `runtime.people`).
  const [people, setPeople] = useState<Record<string, string>>({})
  const alive = useRef(true)

  useEffect(() => () => { alive.current = false }, [])

  const load = useCallback(async () => {
    try {
      const page = await runtime.list({ limit: 200, ...(scope === 'all' ? { all: true } : {}) })
      if (!alive.current) return
      setItems(page.items || [])
      setCounts({ total: page.total, by_kind: page.by_kind || {}, overdue: page.overdue || 0 })
      // Only the caller's OWN queue drives the top-bar badge: a badge showing
      // the tenant's whole backlog would tell an admin nothing about their day.
      if (scope === 'mine') onCountChange?.(page.total || 0)
      if (typeof page.is_super_approver === 'boolean') setIsSuper(page.is_super_approver)
      setError('')
    } catch (err) {
      if (!alive.current) return
      // A worklist that fails silently is worse than one that says so: the
      // user would read an empty list as "nothing to do".
      setError(err instanceof Error ? err.message : 'Could not load your work.')
    } finally {
      if (alive.current) setLoading(false)
    }
  }, [runtime, onCountChange, scope])

  useEffect(() => { void load() }, [load])

  useEffect(() => {
    if (!isSuper || scope !== 'mine' || loading || items.length > 0) {
      setElsewhere(null)
      return undefined
    }
    let live = true
    void (async () => {
      try {
        const page = await runtime.list({ limit: 1, all: true })
        if (live) setElsewhere(page.total || 0)
      } catch { /* the hint is a bonus, never a failure */ }
    })()
    return () => { live = false }
  }, [isSuper, scope, loading, items.length, runtime])

  // Name the documents behind any document rows in the current list. Exactly
  // what the approvals inbox does, through the same repository call, so one
  // document reads the same in both places. A row that cannot be named is
  // still a row that can be opened.
  useEffect(() => {
    const repos = Array.from(new Set(items
      .filter((t) => t.target?.dms)
      .map((t) => String(t.target?.repo || ''))
      .filter(Boolean)))
    if (repos.length === 0 || !runtime.documents) { setDocRows({}); return undefined }
    let live = true
    void (async () => {
      try {
        const rows = await runtime.documents!(repos)
        if (live) setDocRows(rows || {})
      } catch {
        // A repository the caller may not list is not a worklist failure.
      }
    })()
    return () => { live = false }
  }, [items, runtime])

  // Name whoever ASKED for each piece of work. The index stores an id — the
  // directories that turn one into a person are not readable from it — so
  // without this the row said "Asked by 690b81b9-00af-…", which identifies
  // nobody. Best-effort: an id that resolves to nothing is simply not shown.
  useEffect(() => {
    const wanted = items.some((t) => String(t.requested_by || '').trim())
    if (!wanted || !runtime.people) { setPeople({}); return undefined }
    const repos = Array.from(new Set(items
      .map((t) => String(t.target?.repo || ''))
      .filter(Boolean)))
    let live = true
    void (async () => {
      try {
        const found = await runtime.people!(repos)
        if (live) setPeople(found || {})
      } catch {
        // A directory the caller may not read is not a worklist failure.
      }
    })()
    return () => { live = false }
  }, [items, runtime])

  useEffect(() => {
    // Not while the approvals tab is open: re-reading the list underneath it
    // buys nothing and its own panel refreshes itself.
    if (!pollMs || view !== 'work') return undefined
    const id = window.setInterval(() => { void load() }, pollMs)
    const onFocus = () => { void load() }
    window.addEventListener('focus', onFocus)
    return () => { window.clearInterval(id); window.removeEventListener('focus', onFocus) }
  }, [load, pollMs, view])

  const shown = useMemo(() => {
    const spec = FILTERS.find((f) => f.key === filter)
    if (!spec?.kinds) return items
    return items.filter((t) => spec.kinds!.includes(t.kind))
  }, [items, filter])

  const countFor = useCallback((f: typeof FILTERS[number]): number => {
    if (!counts) return 0
    if (!f.kinds) return counts.total
    return f.kinds.reduce((n, k) => n + (counts.by_kind[k] || 0), 0)
  }, [counts])

  const open = useCallback(async (task: WorkTask) => {
    setNotice(null)
    setBusyKey(task.task_key)
    try {
      // The seed is fetched by key rather than carried in the row, so a link
      // stays short and the server re-checks the caller's right on every open.
      let seed
      const kind = task.target?.open
      if (kind === 'playbook_input' && playbookTasks) {
        setPbTask(task)
        setView('playbook')
        return
      }
      // A paused wizard run picks up where it stopped (its stack, record and
      // the pending call's outcome come from the server's run store).
      const runId = String(task.target?.run_id || '')
      if (kind === 'wizard_resume' && runId && onResumeWizard) {
        const wizard = String(task.target?.wizard || task.target?.obj_name || task.obj_name || '')
        await onResumeWizard({ wizard, runId, task })
        return
      }
      if (kind === 'create' || kind === 'record' || kind === 'action') {
        try { seed = await runtime.seed(task.task_key) } catch { /* open anyway */ }
      }
      // An approval (or a document approval — same table, same decision
      // surface) selects the record in this panel's own Approvals tab. Only
      // work that leads somewhere ELSE goes back to the host to navigate.
      // Only a row backed by an approval INSTANCE has anything to review in
      // the inbox. A document merely waiting for its sign-off has no instance
      // yet — it opens in its repository, where the reviewer starts one.
      const instanceId = String(task.target?.instance_id || '')
      const decision = !!instanceId && (kind === 'decide' || task.kind === 'approval'
        || task.kind === 'document' || task.kind === 'document_review')
      if (decision && renderApprovals) {
        setApprovalAt(instanceId)
        setView('approvals')
        return
      }
      // A document with no instance yet opens IN the panel: its viewer, its
      // metadata and its lifecycle buttons, with the rest of the queue one
      // click away.
      const repo = String(task.target?.repo || '')
      const documentId = String(task.target?.document_id || task.record_key || '')
      if (task.target?.dms && repo && documentId && renderDocument) {
        setDocAt({ repo, documentId })
        setView('document')
        return
      }
      await onOpenTask(task, seed, { scope })
    } catch (err) {
      runtime.notify?.(err instanceof Error ? err.message : 'Could not open that task.', 'error')
    } finally {
      if (alive.current) setBusyKey('')
    }
  }, [onOpenTask, runtime, scope, renderApprovals, renderDocument, playbookTasks, onResumeWizard])

  /**
   * "Approve" straight from the row. Anything the list cannot carry — a
   * validation failure, warnings to acknowledge, a document decision — falls
   * through to the review surface with the reason said out loud, so the
   * shortcut never becomes a dead end.
   */
  const approve = useCallback(async (task: WorkTask) => {
    if (!runtime.approve) { void open(task); return }
    setBusyKey(task.task_key)
    try {
      const res = await runtime.approve(task)
      if (res?.ok) {
        if (res.message) setNotice({ text: res.message, recordKey: res.next_record })
        else runtime.notify?.('Approved.', 'success')
        await load()
        return
      }
      // The decision has to be made, just not from a list.
      runtime.notify?.(res?.reason || 'That approval needs a closer look.', 'info')
      await onOpenTask(task, undefined, { scope })
    } catch (err) {
      // A refusal usually means the row is stale — somebody decided it in the
      // inbox, or this is a second click on one already approved. Re-reading
      // the list is the honest answer: the row goes if it is gone. Sending the
      // user to the inbox to look for an approval that is no longer there
      // would be worse than the error itself.
      runtime.notify?.(
        err instanceof Error ? err.message : 'Could not approve that.', 'error')
      await load()
    } finally {
      if (alive.current) setBusyKey('')
    }
  }, [runtime, load, onOpenTask, scope, open])

  const tick = useCallback(async (task: WorkTask) => {
    setBusyKey(task.task_key)
    try {
      await runtime.complete(task.task_key)
      runtime.notify?.('Done.', 'success')
      await load()
    } catch (err) {
      // Only an owned task can be ticked off; the server explains why, and
      // relaying that verbatim is more useful than "something went wrong".
      runtime.notify?.(err instanceof Error ? err.message : 'Could not close that task.', 'error')
    } finally {
      if (alive.current) setBusyKey('')
    }
  }, [runtime, load])

  return (
    <div
      role="dialog"
      aria-label="Your work"
      style={{
        // Above the shell's sticky top bar (z 9000), matching the
        // approvals inbox — otherwise the panel's own header, and with it
        // the close button, hides behind the app header.
        position: 'fixed', inset: 0, zIndex: 9430,
        display: 'flex', justifyContent: 'flex-end',
        background: 'rgba(15,23,42,0.35)',
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          // ONE width for every tab. The approvals surface is a list plus the
          // staged record side by side and the document tab is a viewer, so
          // the panel is sized for those — and it does not resize underneath
          // somebody who is only changing tabs, which reads as the panel
          // having been replaced.
          width: 'min(1180px, 100%)',
          height: '100%',
          display: 'flex', flexDirection: 'column',
          background: 'var(--color-bg, #fff)',
          borderLeft: '1px solid var(--color-border, #e2e8f0)',
          boxShadow: '-12px 0 32px rgba(15,23,42,0.12)',
        }}
      >
        {/* header */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10,
          padding: '0.9rem 1rem',
          borderBottom: '1px solid var(--color-border, #e2e8f0)',
        }}>
          <ClipboardList size={18} />
          <strong style={{ fontSize: '1.0425rem' }}>
            {view === 'approvals' ? 'Approvals'
              : view === 'document' ? 'Document'
              : view === 'playbook' ? 'Agent request'
              : scope === 'all' ? 'All work' : 'Your work'}
          </strong>
          {view !== 'work' && (
            <button
              type="button"
              onClick={() => {
                setView('work'); setApprovalAt(''); setDocAt(null); setPbTask(null); void load()
              }}
              title="Back to your work"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 4,
                padding: '0.2rem 0.6rem', borderRadius: 999, cursor: 'pointer',
                fontSize: '0.8125rem', fontWeight: 700,
                border: '1px solid var(--color-border, #e2e8f0)',
                background: 'transparent', color: 'var(--color-text-secondary, #475569)',
              }}
            >
              <ArrowLeft size={12} /> Work
            </button>
          )}
          {isSuper && (
            <div style={{ display: 'inline-flex', gap: 2, marginLeft: 2 }}>
              {(['mine', 'all'] as const).map((s) => (
                <button
                  key={s} type="button"
                  onClick={() => { if (s !== scope) { setLoading(true); setScope(s) } }}
                  title={s === 'all'
                    ? "Everything open in this app, whoever it is waiting on"
                    : 'Only what is waiting on you'}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 4,
                    padding: '0.2rem 0.55rem', borderRadius: 999, cursor: 'pointer',
                    fontSize: '0.8125rem', fontWeight: 700,
                    border: `1px solid ${scope === s ? 'var(--color-accent, #4c8dff)' : 'var(--color-border, #e2e8f0)'}`,
                    background: scope === s ? 'var(--color-accent, #4c8dff)' : 'transparent',
                    color: scope === s ? '#fff' : 'var(--color-text-secondary, #475569)',
                  }}
                >
                  {s === 'all' && <Users size={12} />}
                  {s === 'all' ? 'Everyone' : 'Mine'}
                </button>
              ))}
            </div>
          )}
          {counts && counts.overdue > 0 && (
            <span style={{
              fontSize: '0.8125rem', fontWeight: 700, padding: '2px 8px', borderRadius: 999,
              background: 'var(--color-error-bg, #fee2e2)',
              color: 'var(--color-error, #b91c1c)',
            }}>
              {counts.overdue} overdue
            </span>
          )}
          <span style={{ flex: 1 }} />
          {runtime.principals && view === 'work' && (
            <button
              type="button"
              onClick={() => setAssignOpen(true)}
              title="Give somebody a task — or yourself one"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 5,
                padding: '0.3rem 0.7rem', borderRadius: 999, cursor: 'pointer',
                fontSize: '0.8125rem', fontWeight: 700, whiteSpace: 'nowrap',
                border: '1px solid var(--color-accent, #4c8dff)',
                background: 'transparent', color: 'var(--color-accent, #4c8dff)',
              }}
            >
              <UserPlus size={13} /> New task
            </button>
          )}
          <button
            type="button" title="Refresh"
            onClick={() => { if (view === 'work') void load() }}
            style={{ ...iconBtn, opacity: view === 'work' ? 1 : 0.45 }}
          >
            <RefreshCw size={15} />
          </button>
          <button type="button" onClick={onClose} title="Close" style={iconBtn}>
            <X size={16} />
          </button>
        </div>

        {/* filters */}
        <div style={{
          display: 'flex', gap: 6, flexWrap: 'wrap', padding: '0.6rem 1rem',
          borderBottom: '1px solid var(--color-border, #e2e8f0)',
        }}>
          {FILTERS.map((f) => {
            const n = countFor(f)
            const active = filter === f.key
            const isTab = f.key === 'approval' && !!renderApprovals
            const on = isTab ? view === 'approvals' : (view === 'work' && active)
            return (
              <button
                key={f.key} type="button"
                title={isTab ? 'Approvals — review and decide' : undefined}
                onClick={() => {
                  // The approvals pill is a TAB when the host has the real
                  // inbox: deciding needs the staged record, not a summary.
                  // Every other pill walks back to the list.
                  if (isTab) { setApprovalAt(''); setView('approvals'); return }
                  if (view !== 'work') { setView('work'); setDocAt(null); void load() }
                  setFilter(f.key)
                }}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  padding: '0.3rem 0.7rem', borderRadius: 999,
                  fontSize: '0.8425rem', fontWeight: 600, cursor: 'pointer',
                  border: `1px solid ${on ? 'var(--color-accent, #4c8dff)' : 'var(--color-border, #e2e8f0)'}`,
                  background: on ? 'var(--color-accent, #4c8dff)' : 'transparent',
                  color: on ? '#fff' : 'var(--color-text-secondary, #475569)',
                }}
              >
                {f.label}
                {n > 0 && <span style={{ opacity: 0.85, fontWeight: 700 }}>{n}</span>}
              </button>
            )
          })}
        </div>

        {/* body — the approvals tab hands its whole area to the host's inbox */}
        {view === 'playbook' && playbookTasks && pbTask ? (
          <div style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: '1rem' }}>
            <PlaybookForm
              title={pbTask.title}
              intro={pbTask.summary || (pbTask.requested_by ? `Requested by ${pbTask.requested_by}.` : '')}
              fields={((pbTask.target?.form as { fields?: FormField[] } | undefined)?.fields) || []}
              mode={String((pbTask.target?.form as { mode?: string } | undefined)?.mode || 'input')}
              context={pbTask.context || undefined}
              submitLabel="Send answer"
              onSubmit={async (answer) => {
                await playbookTasks.answer(String(pbTask.target?.source_ref || pbTask.source_ref), answer)
                runtime.notify?.('Answer sent — the agent continues.', 'success')
                setView('work'); setPbTask(null); void load()
              }}
            />
          </div>
        ) : view === 'document' && renderDocument && docAt ? (
          <div style={{ flex: 1, minHeight: 0, display: 'flex', overflow: 'auto' }}>
            {renderDocument(docAt)}
          </div>
        ) : view === 'approvals' && renderApprovals ? (
          <div style={{ flex: 1, minHeight: 0, display: 'flex' }}>
            {renderApprovals({ instanceId: approvalAt, scope })}
          </div>
        ) : (
        <div style={{ flex: 1, overflowY: 'auto', padding: '0.6rem' }}>
          {notice && (
            <div style={{
              display: 'flex', alignItems: 'flex-start', gap: 8,
              padding: '0.6rem 0.75rem', marginBottom: 8, borderRadius: 10,
              border: '1px solid var(--color-accent, #4c8dff)',
              background: 'var(--color-accent-weak, rgba(76,141,255,0.10))',
              fontSize: '0.8625rem', lineHeight: 1.5,
            }}>
              <Info size={15} style={{ marginTop: 2, flexShrink: 0 }} />
              <span style={{ flex: 1 }}>{notice.text}</span>
              <button
                type="button" onClick={() => setNotice(null)} title="Dismiss"
                style={{
                  border: 'none', background: 'transparent', cursor: 'pointer',
                  color: 'var(--color-text-secondary, #475569)', padding: 0,
                }}
              >
                <X size={14} />
              </button>
            </div>
          )}
          {loading && (
            <div style={centered}><Loader2 size={18} className="spin" /> Loading your work…</div>
          )}
          {!loading && error && (
            <div style={{ ...centered, color: 'var(--color-error, #b91c1c)' }}>{error}</div>
          )}
          {!loading && !error && shown.length === 0 && (
            <div style={{ ...centered, flexDirection: 'column', gap: 10 }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                <CheckCircle2 size={18} />
                {filter !== 'all' ? 'Nothing here right now.'
                  : scope === 'all' ? 'Nothing is open in this app.'
                  : 'Nothing is waiting on you.'}
              </span>
              {scope === 'mine' && elsewhere !== null && elsewhere > 0 && (
                // A worklist is scoped to what is waiting ON YOU: work routed
                // to a role you do not hold is somebody else's, and saying so
                // is the difference between an empty panel and a broken one.
                <span style={{ textAlign: 'center', lineHeight: 1.6 }}>
                  {elsewhere} {elsewhere === 1 ? 'item is' : 'items are'} open in this
                  app, waiting on other people or roles.
                  <br />
                  <button
                    type="button"
                    onClick={() => { setLoading(true); setScope('all') }}
                    style={{
                      marginTop: 6, padding: '0.25rem 0.7rem', borderRadius: 999,
                      fontSize: '0.8225rem', fontWeight: 700, cursor: 'pointer',
                      border: '1px solid var(--color-accent, #4c8dff)',
                      background: 'transparent', color: 'var(--color-accent, #4c8dff)',
                    }}
                  >
                    Show everyone&rsquo;s work
                  </button>
                </span>
              )}
              {runtime.principals && filter !== 'approval' && (
                <button
                  type="button"
                  onClick={() => setAssignOpen(true)}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    padding: '0.3rem 0.8rem', borderRadius: 999, cursor: 'pointer',
                    fontSize: '0.8225rem', fontWeight: 700,
                    border: '1px solid var(--color-border, #e2e8f0)',
                    background: 'transparent',
                    color: 'var(--color-text-secondary, #475569)',
                  }}
                >
                  <UserPlus size={13} /> Give somebody a task
                </button>
              )}
            </div>
          )}
          {!loading && !error && shown.map((task) => {
            const Icon = iconFor(task.kind)
            const overdue = task.sla_state === 'overdue'
            const soon = task.sla_state === 'due_soon'
            const issues = task.seed_issues || []
            const owned = task.task_class === 'C'
            const busy = busyKey === task.task_key
            // The row the last action handed the work to: the one the notice
            // above is talking about.
            const pointed = !!notice?.recordKey
              && String(task.record_key || '') === notice.recordKey
            // An approval row's primary button DECIDES; the rest of the card
            // opens the review surface, so "yes, fine" is one click and
            // "let me look first" is any other click.
            // A document RELEASE decides like a transaction approval; a
            // document REVIEW asks to be read first, and its button says so.
            // A transaction approval decides through the approval engine; a
            // document decides through its repository — either an instance to
            // sign off, or the lifecycle action that raises one.
            const decides = !!runtime.approve
              && (task.kind === 'approval' || task.kind === 'document')
              && (!!task.target?.instance_id || !!task.target?.action)
            const doc = task.target?.dms
              ? docRows[String(task.target?.instance_id || '')]
              : undefined
            const meta = approvalMeta(task, doc)
            return (
              <div
                key={task.task_key}
                role="button"
                tabIndex={0}
                title="Open this for review"
                onClick={() => { if (!busy) void open(task) }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); if (!busy) void open(task) }
                }}
                style={{
                  display: 'flex', gap: 10, padding: '0.75rem',
                  borderRadius: 10, marginBottom: 6, cursor: 'pointer',
                  border: `1px solid ${pointed
                    ? 'var(--color-accent, #4c8dff)' : 'var(--color-border, #e2e8f0)'}`,
                  boxShadow: pointed
                    ? '0 0 0 3px var(--color-accent-weak, rgba(76,141,255,0.18))' : 'none',
                  background: 'var(--color-bg-secondary, #f8fafc)',
                }}
              >
                <Icon size={16} style={{ marginTop: 2, flexShrink: 0, opacity: 0.75 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: '0.9425rem' }}>
                    {/* Once the repository has named the document, the row
                        leads with the DOCUMENT — every row in a repository
                        otherwise reads identically. */}
                    {doc?.title ? `${task.verb || 'Open'} ${doc.title}` : task.title}
                  </div>
                  {/* What is staged, as the inbox previews it — the same
                      label: value chips, computed once by the provider so the
                      two surfaces cannot drift apart. */}
                  {meta.fields.length > 0 ? (
                    <div style={{
                      display: 'flex', flexWrap: 'wrap', gap: '2px 10px', marginTop: 3,
                      fontSize: '0.8225rem',
                    }}>
                      {meta.fields.map((f, i) => (
                        <span key={`${f.label}-${i}`} style={{ color: 'var(--color-text-secondary, #64748b)' }}>
                          <span style={{ opacity: 0.7 }}>{f.label}:</span>{' '}
                          <span style={{ color: 'var(--color-text, #0f172a)' }}>{f.value}</span>
                        </span>
                      ))}
                    </div>
                  ) : task.summary ? (
                    <div style={{
                      fontSize: '0.8425rem', marginTop: 2,
                      color: 'var(--color-text-secondary, #64748b)',
                    }}>{task.summary}</div>
                  ) : null}
                  <div style={{
                    display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 6,
                    fontSize: '0.8125rem', color: 'var(--color-text-secondary, #64748b)',
                  }}>
                    {task.due_at && (
                      <span style={{
                        fontWeight: overdue || soon ? 700 : 500,
                        color: overdue ? 'var(--color-error, #b91c1c)'
                          : soon ? 'var(--color-warning, #b45309)' : undefined,
                      }}>
                        Due {dueLabel(task.due_at)}
                      </span>
                    )}
                    {/* Never a bare id. A key the directory cannot place is
                        shown only when a person could read it anyway (a login,
                        an email); a UUID is dropped, because "Asked by
                        690b81b9-00af-…" answers the question with noise. */}
                    {(() => {
                      const by = meta.submitter
                        ? people[meta.submitter] || readablePerson(meta.submitter) : ''
                      const asked = task.requested_by
                        ? people[task.requested_by] || readablePerson(task.requested_by) : ''
                      if (by) return <span>by {by}</span>
                      return asked ? <span>Asked by {asked}</span> : null
                    })()}
                    {meta.when && <span>· {meta.when}</span>}
                    {meta.request && (
                      <span style={{ textTransform: 'capitalize' }}>· {meta.request}</span>
                    )}
                    {meta.step && (
                      <span style={{
                        padding: '1px 7px', borderRadius: 10,
                        border: '1px solid var(--color-border, #e2e8f0)',
                        background: 'var(--color-bg, #fff)',
                      }}>{meta.step}</span>
                    )}
                    {scope === 'all' && waitingOn(task) && (
                      // In the administrator's view the useful column is not
                      // "what is this" but "whose desk is it on".
                      <span style={{ fontWeight: 600 }}>With {waitingOn(task)}</span>
                    )}
                    {issues.length > 0 && (
                      <span style={{ color: 'var(--color-warning, #b45309)', fontWeight: 600 }}>
                        {/* Name the fields: it tells the assignee whether this
                            is ten seconds of work or a phone call. */}
                        {issues.some((i) => i.field)
                          ? `Needs ${[...new Set(issues.map((i) => i.field).filter(Boolean))].join(', ')}`
                          : `${issues.length} field${issues.length === 1 ? '' : 's'} need your input`}
                      </span>
                    )}
                  </div>
                </div>
                <div
                  onClick={(e) => e.stopPropagation()}
                  style={{ display: 'flex', flexDirection: 'column', gap: 6, flexShrink: 0 }}
                >
                  <button
                    type="button" disabled={busy}
                    title={decides ? 'Approve without opening the record' : undefined}
                    onClick={() => { void (decides ? approve(task) : open(task)) }}
                    style={{
                      padding: '0.35rem 0.75rem', borderRadius: 8, cursor: 'pointer',
                      fontSize: '0.8425rem', fontWeight: 700, whiteSpace: 'nowrap',
                      border: '1px solid var(--color-accent, #4c8dff)',
                      background: 'var(--color-accent, #4c8dff)', color: '#fff',
                      opacity: busy ? 0.6 : 1,
                    }}
                  >
                    {/* The button says what happens, never a generic "Open". */}
                    {task.verb || task.target?.verb || 'Open'}
                  </button>
                  {owned && (
                    <button
                      type="button" disabled={busy} onClick={() => { void tick(task) }}
                      style={{
                        padding: '0.3rem 0.7rem', borderRadius: 8, cursor: 'pointer',
                        fontSize: '0.8125rem', fontWeight: 600, whiteSpace: 'nowrap',
                        border: '1px solid var(--color-border, #e2e8f0)',
                        background: 'transparent',
                        color: 'var(--color-text-secondary, #475569)',
                      }}
                    >
                      Done
                    </button>
                  )}
                </div>
              </div>
            )
          })}
        </div>
        )}
      </div>
      {assignOpen && (
        <AssignTaskDialog
          runtime={runtime}
          onClose={() => setAssignOpen(false)}
          // A task you made for yourself is in your list the moment you make
          // it; one you gave away is not, so the reload is what keeps the
          // panel honest either way.
          onCreated={() => { void load() }}
        />
      )}
    </div>
  )
}

const iconBtn: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  width: 30, height: 30, borderRadius: 8, cursor: 'pointer',
  border: '1px solid var(--color-border, #e2e8f0)',
  background: 'transparent', color: 'var(--color-text-secondary, #475569)',
}

const centered: React.CSSProperties = {
  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
  padding: '2.5rem 1rem', fontSize: '0.9125rem',
  color: 'var(--color-text-secondary, #64748b)',
}
